import { useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Link, useParams } from 'react-router-dom';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@artstorefronts/ui';
import { ExternalLink, Eye, EyeOff, GripVertical, Loader2, Trash2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

import AdminLayout from '../../../components/AdminLayout';
import {
  ADMIN_ADD_PRE_FEED_POST,
  PRIORITY_MODE_OPTIONS,
  ADMIN_GENERATE_PRE_FEED_POSTS,
  ADMIN_PRE_FEED,
  ADMIN_REMOVE_PRE_FEED_POST,
  ADMIN_SAVE_PRE_FEED_POSTS,
  ADMIN_SEARCH_COMMUNITY_POSTS,
  ADMIN_UPDATE_PRE_FEED,
  COMMUNITY_POST_BY_SLUG,
  STATUS_PILL_CLASSES,
  listToText,
  textToList,
  type AdminPreFeed,
  type AdminPreFeedPost,
} from './pre-feed-shared';

const TOWN_SQUARE_URL = typeof __TOWN_SQUARE_URL__ !== 'undefined' ? __TOWN_SQUARE_URL__ : '';

interface EditorPostRow {
  id: string;
  postId: string;
  enabled: boolean;
  recommendedRank: number | null;
  title: string;
  slug: string | null;
  communitySlug: string | null;
  communityName: string;
  voteScore: number;
  commentCount: number;
}

interface SettingsForm {
  name: string;
  priority: string;
  priorityMode: string;
  intents: string;
  careerStages: string;
  countryCodes: string;
  signedUpBefore: string;
  maxVotesCast: string;
  maxCommentsWritten: string;
  maxPostsRead: string;
  minComments: string;
  minVotes: string;
  minEngagement: string;
  maxPosts: string;
}

function toForm(feed: AdminPreFeed): SettingsForm {
  return {
    name: feed.name,
    priority: String(feed.priority ?? 0),
    priorityMode: feed.priorityMode ?? 'HIGH_AND_NORMAL_FIRST',
    intents: listToText(feed.audience?.intents),
    careerStages: listToText(feed.audience?.careerStages),
    countryCodes: listToText(feed.audience?.countryCodes),
    signedUpBefore: feed.exclusions?.signedUpBefore ? feed.exclusions.signedUpBefore.slice(0, 10) : '',
    maxVotesCast: feed.exclusions?.maxVotesCast != null ? String(feed.exclusions.maxVotesCast) : '',
    maxCommentsWritten: feed.exclusions?.maxCommentsWritten != null ? String(feed.exclusions.maxCommentsWritten) : '',
    maxPostsRead: feed.exclusions?.maxPostsRead != null ? String(feed.exclusions.maxPostsRead) : '',
    minComments: feed.sourceCriteria?.minComments != null ? String(feed.sourceCriteria.minComments) : '',
    minVotes: feed.sourceCriteria?.minVotes != null ? String(feed.sourceCriteria.minVotes) : '',
    minEngagement: feed.sourceCriteria?.minEngagement != null ? String(feed.sourceCriteria.minEngagement) : '',
    maxPosts: feed.sourceCriteria?.maxPosts != null ? String(feed.sourceCriteria.maxPosts) : '',
  };
}

function optionalInt(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? Math.floor(value) : null;
}

function toUpdateInput(form: SettingsForm) {
  return {
    name: form.name.trim(),
    priority: optionalInt(form.priority) ?? 0,
    priorityMode: form.priorityMode,
    audience: {
      intents: textToList(form.intents),
      careerStages: textToList(form.careerStages),
      countryCodes: textToList(form.countryCodes.toUpperCase()),
    },
    exclusions: {
      signedUpBefore: form.signedUpBefore.trim() ? new Date(form.signedUpBefore.trim()).toISOString() : null,
      maxVotesCast: optionalInt(form.maxVotesCast),
      maxCommentsWritten: optionalInt(form.maxCommentsWritten),
      maxPostsRead: optionalInt(form.maxPostsRead),
    },
    sourceCriteria: {
      minComments: optionalInt(form.minComments),
      minVotes: optionalInt(form.minVotes),
      minEngagement: optionalInt(form.minEngagement),
      maxPosts: optionalInt(form.maxPosts),
    },
  };
}

function mapServerPost(row: AdminPreFeedPost): EditorPostRow {
  return {
    id: row.id,
    postId: row.postId,
    enabled: row.enabled,
    recommendedRank: row.recommendedRank ?? null,
    title: row.post?.title ?? `Post ${row.postId}`,
    slug: row.post?.slug ?? null,
    communitySlug: row.post?.community?.slug ?? null,
    communityName: row.post?.community?.name ?? '—',
    voteScore: row.post?.voteScore ?? 0,
    commentCount: row.post?.commentCount ?? 0,
  };
}

function parsePostUrl(url: string): { communitySlug: string; postSlug: string } | null {
  try {
    const u = new URL(url.trim());
    const match = u.pathname.match(/\/(?:[a-z]{2}\/)?community\/([^/]+)\/post\/([^/]+)/);
    if (match) {
      return { communitySlug: match[1], postSlug: match[2] };
    }
  } catch {
    const match = url.trim().match(/\/(?:[a-z]{2}\/)?community\/([^/]+)\/post\/([^/]+)/);
    if (match) {
      return { communitySlug: match[1], postSlug: match[2] };
    }
  }
  return null;
}

function SortablePostRow({
  row,
  index,
  onToggle,
  onRemove,
}: {
  row: EditorPostRow;
  index: number;
  onToggle: (id: string, enabled: boolean) => void;
  onRemove: (row: EditorPostRow) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const postUrl =
    row.communitySlug && row.slug && TOWN_SQUARE_URL
      ? `${TOWN_SQUARE_URL}/community/${row.communitySlug}/post/${row.slug}`
      : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`grid grid-cols-[32px_50px_60px_1fr_140px_60px_70px_60px_40px] items-center gap-2 border-b border-gray-100 bg-white px-3 py-2 text-sm last:border-b-0 ${row.enabled ? '' : 'opacity-60'}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="flex cursor-grab touch-none items-center justify-center active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4 text-gray-400" />
      </div>
      <div className="text-gray-600">{index + 1}</div>
      <div className="text-gray-400" title="Recommended rank (algorithmic order at generation time)">
        {row.recommendedRank ?? '—'}
      </div>
      <div className="min-w-0 truncate font-medium text-gray-900" title={row.title}>
        {postUrl ? (
          <a
            href={postUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 hover:underline"
          >
            <span className="truncate">{row.title}</span>
            <ExternalLink className="h-3 w-3 shrink-0 text-gray-400" />
          </a>
        ) : (
          row.title
        )}
      </div>
      <div className="truncate text-gray-500" title={row.communityName}>
        {row.communityName}
      </div>
      <div className="text-gray-600">{row.voteScore}</div>
      <div className="text-gray-600">{row.commentCount}</div>
      <div>
        <Switch checked={row.enabled} onCheckedChange={(checked) => onToggle(row.id, checked === true)} />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => onRemove(row)}
        aria-label="Remove from pre-feed"
        className="text-gray-400 hover:text-red-600"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function AdminPreFeedEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [rows, setRows] = useState<EditorPostRow[]>([]);
  const [savedRows, setSavedRows] = useState<EditorPostRow[]>([]);
  const [savedForm, setSavedForm] = useState<SettingsForm | null>(null);
  const [addText, setAddText] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [confirmGenerate, setConfirmGenerate] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<EditorPostRow | null>(null);
  const isDirtyRef = useRef(false);

  const { data, loading, refetch } = useQuery(ADMIN_PRE_FEED, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
  });
  const [updatePreFeed, { loading: savingSettings }] = useMutation(ADMIN_UPDATE_PRE_FEED);
  const [savePosts, { loading: savingPosts }] = useMutation(ADMIN_SAVE_PRE_FEED_POSTS);
  const [generatePosts, { loading: generating }] = useMutation(ADMIN_GENERATE_PRE_FEED_POSTS);
  const [addPost, { loading: adding }] = useMutation(ADMIN_ADD_PRE_FEED_POST);
  const [removePost, { loading: removing }] = useMutation(ADMIN_REMOVE_PRE_FEED_POST);
  const [resolveBySlug, { loading: resolving }] = useLazyQuery(COMMUNITY_POST_BY_SLUG);
  const [searchPosts, { loading: searching }] = useLazyQuery(ADMIN_SEARCH_COMMUNITY_POSTS);

  const feed: (AdminPreFeed & { posts?: AdminPreFeedPost[] }) | null = data?.adminPreFeed ?? null;

  useEffect(() => {
    if (!feed || isDirtyRef.current) return;
    const mappedForm = toForm(feed);
    const mappedRows = (feed.posts ?? []).map(mapServerPost);
    setForm(mappedForm);
    setSavedForm(mappedForm);
    setRows(mappedRows);
    setSavedRows(mappedRows);
  }, [feed]);

  const hasUnsavedChanges = useMemo(() => {
    if (!form || !savedForm) return false;
    if (JSON.stringify(form) !== JSON.stringify(savedForm)) return true;
    return (
      JSON.stringify(rows.map((r) => ({ id: r.id, enabled: r.enabled }))) !==
      JSON.stringify(savedRows.map((r) => ({ id: r.id, enabled: r.enabled })))
    );
  }, [form, savedForm, rows, savedRows]);

  useEffect(() => {
    isDirtyRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  function setField(field: keyof SettingsForm, value: string) {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
    isDirtyRef.current = true;
  };

  async function handleSaveAll() {
    if (!feed || !form) return;
    try {
      await updatePreFeed({ variables: { id: feed.id, input: toUpdateInput(form) } });
      await savePosts({
        variables: {
          preFeedId: feed.id,
          posts: rows.map((row, index) => ({ id: row.id, sortOrder: index + 1, enabled: row.enabled })),
        },
      });
      isDirtyRef.current = false;
      toast.success('Pre-feed saved.');
      void refetch();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to save pre-feed.');
    }
  }

  async function handleGenerate() {
    if (!feed) return;
    try {
      const result = await generatePosts({ variables: { preFeedId: feed.id } });
      setConfirmGenerate(false);
      isDirtyRef.current = false;
      toast.success(`Generated ${result.data?.adminGeneratePreFeedPosts?.postCount ?? ''} posts from criteria.`);
      void refetch();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate posts.');
    }
  }

  async function handleAddById(postId: string) {
    if (!feed) return;
    try {
      await addPost({ variables: { preFeedId: feed.id, postId } });
      setAddText('');
      setSearchResults([]);
      isDirtyRef.current = false;
      toast.success('Post added to pre-feed.');
      void refetch();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to add post.');
    }
  }

  async function handleAddSubmit() {
    const text = addText.trim();
    if (!text) return;

    const parsed = parsePostUrl(text);
    if (parsed) {
      const result = await resolveBySlug({ variables: parsed });
      const post = result.data?.communityPostBySlug;
      if (!post) {
        toast.error('No post found at that URL.');
        return;
      }
      await handleAddById(post.id);
      return;
    }

    const result = await searchPosts({ variables: { search: text } });
    const posts = result.data?.adminCommunityPosts?.posts ?? [];
    if (posts.length === 0) {
      toast.error('No posts matched that search.');
    }
    setSearchResults(posts);
  }

  async function handleConfirmRemove() {
    if (!confirmRemove) return;
    try {
      await removePost({ variables: { id: confirmRemove.id } });
      setRows((prev) => prev.filter((row) => row.id !== confirmRemove.id));
      setSavedRows((prev) => prev.filter((row) => row.id !== confirmRemove.id));
      setConfirmRemove(null);
      toast.success('Post removed from pre-feed.');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove post.');
    }
  }

  if (loading && !feed) {
    return (
      <AdminLayout title="Pre-Feed" description="Loading…">
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  if (!feed || !form) {
    return (
      <AdminLayout title="Pre-Feed" description="Not found">
        <p className="text-sm text-gray-500">
          Pre-feed not found.{' '}
          <Link to="/admin/pre-feeds" className="underline">
            Back to pre-feeds
          </Link>
        </p>
      </AdminLayout>
    );
  }

  const isDraft = feed.status === 'DRAFT';

  return (
    <AdminLayout
      title={feed.name}
      description="Drag to set the serving order. Recommended # is the algorithmic rank at generation time — it never changes as you reorder."
      titleExtra={
        <span className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_PILL_CLASSES[feed.status] ?? ''}`}>
          {feed.status.toLowerCase()}
        </span>
      }
      action={
        <Link to="/admin/pre-feeds" className="text-sm text-gray-500 hover:text-gray-700">
          ← All pre-feeds
        </Link>
      }
    >
      <div className="space-y-8 pb-24">
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="mb-4 text-lg font-medium text-gray-900">Settings</h2>
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <Label htmlFor="pf-name" className="mb-1 block text-sm">
                Name
              </Label>
              <Input id="pf-name" value={form.name} onChange={(e) => setField('name', e.target.value)} />
            </div>
            <div>
              <Label htmlFor="pf-priority" className="mb-1 block text-sm">
                Priority (lowest wins when multiple targeted feeds match)
              </Label>
              <Input
                id="pf-priority"
                type="number"
                value={form.priority}
                onChange={(e) => setField('priority', e.target.value)}
              />
            </div>
          </div>

          <div className="mt-4">
            <Label className="mb-1 block text-sm">Priority posts</Label>
            <Select value={form.priorityMode} onValueChange={(value) => setField('priorityMode', value)}>
              <SelectTrigger className="w-full max-w-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_MODE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-gray-500">
              How posts marked with a trending priority relate to this pre-feed. Note: today only “high” truly
              pins to the top of trending; “normal” is a score floor mixed among organic posts.
            </p>
          </div>

          <h3 className="mb-2 mt-6 text-sm font-medium text-gray-700">
            Audience (leave every field empty for the GLOBAL fallback feed)
          </h3>
          <div className="grid gap-4 lg:grid-cols-3">
            <div>
              <Label htmlFor="pf-intents" className="mb-1 block text-sm">
                Onboarding intents (comma-separated)
              </Label>
              <Input
                id="pf-intents"
                value={form.intents}
                onChange={(e) => setField('intents', e.target.value)}
                placeholder="e.g. connect, learn, feedback"
              />
              <p className="mt-1 text-xs text-gray-500">Known ids: social, sell, connect, learn, feedback, organize, inspire, other</p>
            </div>
            <div>
              <Label htmlFor="pf-stages" className="mb-1 block text-sm">
                Career stages (comma-separated)
              </Label>
              <Input
                id="pf-stages"
                value={form.careerStages}
                onChange={(e) => setField('careerStages', e.target.value)}
                placeholder="e.g. exploring, practicing"
              />
              <p className="mt-1 text-xs text-gray-500">exploring · practicing · selling · professional · teaching</p>
            </div>
            <div>
              <Label htmlFor="pf-countries" className="mb-1 block text-sm">
                Country codes (comma-separated)
              </Label>
              <Input
                id="pf-countries"
                value={form.countryCodes}
                onChange={(e) => setField('countryCodes', e.target.value)}
                placeholder="e.g. US, CA"
              />
              <p className="mt-1 text-xs text-gray-500">IP-derived; users with unknown location never match</p>
            </div>
          </div>

          <h3 className="mb-2 mt-6 text-sm font-medium text-gray-700">
            Exclusions (a user matching ANY of these is not shown this feed)
          </h3>
          <div className="grid gap-4 lg:grid-cols-4">
            <div>
              <Label htmlFor="pf-signed-up-before" className="mb-1 block text-sm">
                Signed up before
              </Label>
              <Input
                id="pf-signed-up-before"
                type="date"
                value={form.signedUpBefore}
                onChange={(e) => setField('signedUpBefore', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="pf-max-votes" className="mb-1 block text-sm">
                Votes cast over
              </Label>
              <Input
                id="pf-max-votes"
                type="number"
                min={0}
                value={form.maxVotesCast}
                onChange={(e) => setField('maxVotesCast', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="pf-max-comments" className="mb-1 block text-sm">
                Comments written over
              </Label>
              <Input
                id="pf-max-comments"
                type="number"
                min={0}
                value={form.maxCommentsWritten}
                onChange={(e) => setField('maxCommentsWritten', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="pf-max-read" className="mb-1 block text-sm">
                Pre-feed posts read over
              </Label>
              <Input
                id="pf-max-read"
                type="number"
                min={0}
                value={form.maxPostsRead}
                onChange={(e) => setField('maxPostsRead', e.target.value)}
              />
            </div>
          </div>

          <h3 className="mb-2 mt-6 text-sm font-medium text-gray-700">Generation criteria</h3>
          <div className="grid gap-4 lg:grid-cols-4">
            <div>
              <Label htmlFor="pf-min-comments" className="mb-1 block text-sm">
                Min comments
              </Label>
              <Input
                id="pf-min-comments"
                type="number"
                min={0}
                value={form.minComments}
                onChange={(e) => setField('minComments', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="pf-min-votes" className="mb-1 block text-sm">
                Min votes
              </Label>
              <Input
                id="pf-min-votes"
                type="number"
                min={0}
                value={form.minVotes}
                onChange={(e) => setField('minVotes', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="pf-min-engagement" className="mb-1 block text-sm">
                Min engagement (votes + 2× comments)
              </Label>
              <Input
                id="pf-min-engagement"
                type="number"
                min={0}
                value={form.minEngagement}
                onChange={(e) => setField('minEngagement', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="pf-max-posts" className="mb-1 block text-sm">
                Max posts (default 200)
              </Label>
              <Input
                id="pf-max-posts"
                type="number"
                min={1}
                max={1000}
                value={form.maxPosts}
                onChange={(e) => setField('maxPosts', e.target.value)}
              />
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setConfirmGenerate(true)}
              disabled={!isDraft || generating}
              title={isDraft ? undefined : 'Generation is draft-only — it replaces the whole post list.'}
            >
              <Wand2 className="mr-2 h-4 w-4" />
              Generate posts from criteria
            </Button>
            {!isDraft && (
              <p className="text-xs text-gray-500">Generation is available only while the feed is in draft.</p>
            )}
          </div>

          <h3 className="mb-2 mt-6 text-sm font-medium text-gray-700">Preview as a user (admin-only, on the fly)</h3>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" asChild>
              <a
                href={`${TOWN_SQUARE_URL}/community?previewPreFeed=${feed.id}&persona=included`}
                target="_blank"
                rel="noreferrer"
              >
                <Eye className="mr-2 h-4 w-4" />
                Preview as INCLUDED user
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a
                href={`${TOWN_SQUARE_URL}/community?previewPreFeed=${feed.id}&persona=excluded`}
                target="_blank"
                rel="noreferrer"
              >
                <EyeOff className="mr-2 h-4 w-4" />
                Preview as EXCLUDED user
              </a>
            </Button>
            <p className="text-xs text-gray-500">
              Included = this feed served fresh (no read filtering; reads not recorded). Excluded = confirms the
              feed does not appear. Works for drafts too — no second account needed.
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-medium text-gray-900">Posts ({rows.length})</h2>
            <div className="flex w-full max-w-xl items-center gap-2">
              <Input
                value={addText}
                onChange={(e) => {
                  setAddText(e.target.value);
                  setSearchResults([]);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleAddSubmit();
                  }
                }}
                placeholder="Paste a post URL, or search by title/slug…"
              />
              <Button onClick={handleAddSubmit} disabled={adding || resolving || searching || !addText.trim()}>
                {adding || resolving || searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
              </Button>
            </div>
          </div>

          {searchResults.length > 0 && (
            <div className="mb-4 rounded-lg border border-gray-200">
              <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                Search results — click to add
              </div>
              {searchResults.map((post) => (
                <button
                  key={post.id}
                  type="button"
                  onClick={() => void handleAddById(post.id)}
                  className="flex w-full items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-gray-50"
                >
                  <span className="min-w-0 truncate font-medium text-gray-900">{post.title}</span>
                  <span className="shrink-0 text-xs text-gray-500">
                    {post.community?.name ?? '—'} · {post.voteScore} votes · {post.commentCount} comments
                  </span>
                </button>
              ))}
            </div>
          )}

          {rows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
              No posts yet. Generate from criteria or add posts by URL/search.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <div className="grid grid-cols-[32px_50px_60px_1fr_140px_60px_70px_60px_40px] items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                <div />
                <div>#</div>
                <div title="Recommended rank (algorithmic order at generation time)">Rec #</div>
                <div>Title</div>
                <div>Community</div>
                <div>Votes</div>
                <div>Comments</div>
                <div>On</div>
                <div />
              </div>
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={rows.map((row) => row.id)} strategy={verticalListSortingStrategy}>
                  {rows.map((row, index) => (
                    <SortablePostRow
                      key={row.id}
                      row={row}
                      index={index}
                      onToggle={(rowId, enabled) => {
                        setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, enabled } : r)));
                      }}
                      onRemove={setConfirmRemove}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-gray-200 bg-white/95 backdrop-blur">
        <div className="container mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4">
          <p className="text-sm text-gray-600">
            {hasUnsavedChanges ? 'Unsaved changes · settings, order, and toggles' : 'All changes saved'}
          </p>
          <Button onClick={handleSaveAll} disabled={savingSettings || savingPosts || !hasUnsavedChanges}>
            {savingSettings || savingPosts ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmGenerate} onOpenChange={setConfirmGenerate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Generate posts from criteria?</AlertDialogTitle>
            <AlertDialogDescription>
              This REPLACES the entire post list (including manual sorting, toggles, and hand-added posts) with the
              top posts matching the saved criteria, ranked by engagement. Save your criteria first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={generating}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleGenerate} disabled={generating} className="bg-red-600 hover:bg-red-700">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Replace posts'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmRemove} onOpenChange={(open) => !open && setConfirmRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this post from the pre-feed?</AlertDialogTitle>
            <AlertDialogDescription>
              “{confirmRemove?.title}” will no longer be part of this pre-feed. The post itself is untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              disabled={removing}
              className="bg-red-600 hover:bg-red-700"
            >
              {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

export default AdminPreFeedEditorPage;
